import * as FinancesActions from "./finances.actions";

import {createReducer, on} from "@ngrx/store";
import dayjs from "dayjs";
import {TenFinanceDebtors, TenFinanceSettings} from "../../_models/finances.models";
import {DahPaginationModel, NameResponse} from '@common/models/commonHTTP.models';
import {DahLoadingType} from '@common/components-module/core-components.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const tenFinancesFeatureKey = "finances";

export interface TenFinancesState {
    settings: {
        main: TenFinanceSettings | undefined,
        fromDate: string,
        toDate: string,
        listLoading?: DahLoadingType,
    },
    debtors: {
        list: TenFinanceDebtors[],
        pagination: DahPaginationModel,
        sort: 'APARTMENT' | 'DEBT_DESC',
        listLoading?: DahLoadingType,
    },
    totalDebt?: string,
    buildingId?: string,
    availableBuildings?: NameResponse[],
    selectedTab: 1 | 2
}

export const initialState: TenFinancesState = {
    settings: {
        main: undefined,
        fromDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
        toDate: dayjs().endOf('day').format('YYYY-MM-DD'),
        listLoading: 'listLoading',
    },
    debtors: {
        list: [],
        pagination: {},
        sort: 'APARTMENT',
        listLoading: 'listLoading',
    },
    selectedTab: 1
};

export const tenFinancesReducer = createReducer(
    initialState,

    on(FinancesActions.LoadSettings, (state) => {
        return {
            ...state,
            settings: {
                ...state.settings,
                listLoading: state.settings.main ? undefined : 'listLoading' as DahLoadingType,
            }
        };
    }),

    on(FinancesActions.LoadSettingsSuccess, (state, action) => {
        return {
            ...state,
            settings: {
                ...state.settings,
                main: action.settings,
                listLoading: undefined
            },
            totalDebt: action.settings.debt,
            ...action.settings.availableBuildings?.length ? {
                ...!state.buildingId && {
                    buildingId: action.settings.availableBuildings[0].id
                },
                availableBuildings: action.settings.availableBuildings
            } : {
                buildingId: undefined,
                availableBuildings: []
            }
        };
    }),

    on(FinancesActions.LoadSettingsFailure, (state) => {
        return {
            ...state,
            settings: {
                ...state.settings,
                listLoading: undefined
            }
        };
    }),

    on(FinancesActions.SetDateRange, (state, dateRange) => {
        return {
            ...state,
            settings: {
                ...state.settings,
                main: undefined,
                fromDate: dateRange.from,
                toDate: dateRange.to
            }
        };
    }),

    on(FinancesActions.SetBuildingId, (state, action) => {
        return {
            ...state,
            settings: {
                ...state.settings,
                main: undefined,
            },
            buildingId: action.buildingId
        };
    }),

    on(FinancesActions.SetTab, (state, action) => {
        return {
            ...state,
            selectedTab: action.tab
        };
    }),

    on(FinancesActions.LoadDebtors, (state, action) => {
        return {
            ...state,
            debtors: {
                ...state.debtors,
                listLoading: action.clearState
                    ? 'listLoading'
                    : (state.debtors.list.length ? 'listLoadNext' : 'listLoading') as DahLoadingType,
            }
        };
    }),

    on(FinancesActions.LoadDebtorsSuccess, (state, action) => {
        return {
            ...state,
            debtors: {
                ...state.debtors,
                list: action.pagination.first ? action.items : [...state.debtors.list, ...action.items],
                totalByPages: action.totalByPages,
                pagination: action.pagination,
                listLoading: undefined
            }
        };
    }),

    on(FinancesActions.LoadDebtorsFailure, (state) => {
        return {
            ...state,
            debtors: {
                ...state.debtors,
                listLoading: undefined
            }
        };
    }),

    on(FinancesActions.SetDebtorsSort, (state, action) => {
        return {
            ...state,
            debtors: {
                ...state.debtors,
                sort: action.sort
            }
        };
    }),

    on(SetAllInitialStates, () => ({...initialState}))
);
